import { useEffect, useState } from "react";

import {
  faAngleRight,
  faCheck,
  faCoin,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import toast from "react-hot-toast";
import { Alert, Modal, ModalBody, ModalHeader } from "reactstrap";
import {
  declineOrder,
  getAllOrders,
  getAllOrdersByManager,
  registrarEntrada,
  registrarEntrega,
} from "../../../api/orders";
import OrderViewer from "../../../components/OrderViewer";
import Stepper from "../../../components/Stepper";
import WhiteBox from "../../../components/WhiteBox";
import {
  Button,
  Col2Grid,
  FH,
  Input,
  Label,
  Text,
  Title,
} from "../../main-styles";
import * as S from "./styles";
import Paginator from "../../../components/Paginator";
import { useSelector } from "react-redux";
import { useConfirm } from "../../../components/ConfirmProvider";

function Pedidos() {
  const { roles } = useSelector((state) => state.authReducer.user);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [pedidos, setPedidos] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10000);
  const [id_everest, setIdEverest] = useState("");
  const [toCreate, setToCreate] = useState(null); // This state is not used in the current code, consider removing it if unnecessary

  const [registrando, setRegistrando] = useState(false);
  const [viewDetails, setViewDetails] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [declineReason, setDeclineReason] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  const [centro_de_custo, setCentroDeCusto] = useState("");

  useEffect(() => {
    loadData();
  }, [page, perPage, search, filterStatus]);

  async function loadData() {
    setLoadingMore(true);

    try {
      const filters = {};
      if (search) {
        filters.needle = search;
      }
      if (filterStatus && filterStatus !== "todos") {
        filters.status = filterStatus;
      }
      let dt = roles.includes("admin")
        ? await getAllOrders(page, perPage, filters)
        : await getAllOrdersByManager(page, perPage, filters);
      console.log(dt);
      setPedidos(dt);
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      console.error("Error loading orders:", error);
      toast.error("Erro ao carregar pedidos");
    }
  }

  async function _cancelarPedido(id) {
    // block parent function from being called
    if (registrando) return; // Prevent multiple submissions
    if (declineReason.trim() === "") {
      toast.error("Por favor, informe o motivo do cancelamento.");
      return;
    }
    toast.promise(declineOrder(id, declineReason), {
      loading: "Cancelando pedido...",
      success: (data) => {
        setPedidos((prev) =>
          prev.map((pedido) =>
            pedido.id === id
              ? { ...pedido, current_status: "cancelado" }
              : pedido
          )
        );
        setDeclineReason("");
        setToCreate(null);
        setDeclineModalOpen(false);
        setIdEverest("");
        return "Pedido cancelado com sucesso!";
      },
      error: (error) => {
        console.error("Error cancelling order:", error);
        return error.message || "Erro ao cancelar pedido";
      },
    });
  }

  async function _confirmDelivery(id) {
    // block parent function from being called

    if (registrando) return; // Prevent multiple submissions
    if (
      !(await confirm({
        title: "Confirmar entrega",
        message: "Você tem certeza que deseja confirmar a entrega deste pedido?",
        confirmText: "Confirmar entrega",
      }))
    ) {
      return;
    }
    setRegistrando(true);
    try {
      await registrarEntrega(id); // Assuming the second parameter is not needed for confirming delivery
      toast.success("Entrega confirmada com sucesso!");
      setPedidos((prev) =>
        prev.map((pedido) =>
          pedido.id === id ? { ...pedido, current_status: "entregue" } : pedido
        )
      );
    } catch (error) {
      console.error("Error confirming delivery:", error);
      toast.error(error.message);
    } finally {
      setRegistrando(false);
    }
  }

  function nextPage() {
    setPage((prevPage) => prevPage + 1);
  }
  function prevPage() {
    if (page > 1) {
      setPage((prevPage) => prevPage - 1);
    }
  }

  async function _registrarEntrada() {
    if (registrando) return; // Prevent multiple submissions
    if (!centro_de_custo) {
      toast.error("Por favor, informe a divisão.");
      return;
    }
    // confirma
    if (
      !(await confirm({
        title: "Registrar entrada",
        message: "Você tem certeza que deseja registrar a entrada deste pedido?",
        confirmText: "Registrar entrada",
      }))
    ) {
      return;
    }
    setRegistrando(true);
    try {
      await registrarEntrada(centro_de_custo, toCreate);
      toast.success("Entrada registrada com sucesso!");
      setToCreate(null);
      setIdEverest("");
      setPedidos((prev) =>
        prev.map((pedido) =>
          pedido.id === toCreate
            ? { ...pedido, current_status: "criado", id_everest }
            : pedido
        )
      );
    } catch (error) {
      console.error("Error registering entry:", error);
      toast.error(error.message);
    } finally {
      setRegistrando(false);
    }
  }

  const pending =
    pedidos?.filter((pedido) => pedido.current_status === "pendente") || [];

  if (loading) {
    return (
      <S.Container style={{ marginTop: 20 }}>
        <Title>Pedidos</Title>
        <WhiteBox style={{ padding: 20, textAlign: "center" }}>
          <Text>Carregando pedidos...</Text>
        </WhiteBox>
      </S.Container>
    );
  }

  const showPending = filterStatus === "todos" || filterStatus === "pending";

  return (
    <S.Container>
      <Title>Pedidos</Title>
      {showPending && (
        <WhiteBox style={{ marginBottom: 20 }}>
          <Title>Aguardando entrada</Title>
          <table className="table table-sm">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Data</th>
                <th scope="col">Distribuidor</th>
                <th scope="col">Itens</th>
                <th scope="col">Valor</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((pedido) => {
                return (
                  <tr
                    key={pedido.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setToCreate(pedido.id)}
                  >
                    <td style={{ verticalAlign: "middle" }}>#{pedido.id}</td>
                    <td style={{ verticalAlign: "middle" }}>
                      {moment(pedido.created_at).format("DD/MM/YY HH:mm")}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      {pedido.distribuidor}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>{pedido.itens}</td>
                    <td style={{ verticalAlign: "middle" }}>
                      <FontAwesomeIcon icon={faCoin} /> {pedido.value}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <FontAwesomeIcon icon={faAngleRight} />
                    </td>
                  </tr>
                );
              })}
              {pending.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    Nenhum pedido pendente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </WhiteBox>
      )}
      {/* <WhiteBox>
          <Title>Aguardando Confirmação de entrega</Title>
          <table className="table table-sm">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Data</th>
                <th scope="col">Distribuidor</th>
                <th scope="col">Itens</th>
                <th scope="col">Valor</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {awaiting.map((pedido) => {
                return (
                  <tr
                    key={pedido.id}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      setViewDetails(pedido.id);
                    }}
                  >
                    <td style={{ verticalAlign: "middle" }}>#{pedido.id}</td>
                    <td style={{ verticalAlign: "middle" }}>
                      {moment(pedido.created_at).format("DD/MM/YY HH:mm")}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      {pedido.distribuidor}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>{pedido.itens}</td>
                    <td style={{ verticalAlign: "middle" }}>
                      <FontAwesomeIcon icon={faCoin} /> {pedido.value}
                    </td>
                    <td
                      style={{ verticalAlign: "middle" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        _confirmDelivery(pedido.id);
                      }}
                    >
                      <div
                        style={{
                          padding: 3,
                          borderRadius: 5,
                          backgroundColor: "#4CAF50",
                          width: 30,
                          height: 30,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <FontAwesomeIcon icon={faCheck} color={"white"} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {awaiting.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    Nenhum pedido aguardando confirmação
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </WhiteBox> */}
      <WhiteBox>
        <Title>Todos os pedidos</Title>
        <S.SearchContainer
          style={{
            marginBottom: 20,
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <Input
            type="text"
            placeholder="Buscar por número do pedido, pedido everest, data ou distribuidor"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ margin: 0 }}
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              maxWidth: 150,
              fontSize: 16,
              borderRadius: 4,
              border: "1px solid #ccc",
              padding: "12px 10px",
            }}
          >
            <option value="todos">Todos</option>
            <option value="pendente">Pendente</option>
            <option value="criado">Criado</option>
            <option value="entregue">Entregue</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </S.SearchContainer>
        <table className="table table-sm">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Data</th>
              <th scope="col">Distribuidor</th>
              <th scope="col">Itens</th>
              <th scope="col">Status</th>
              <th scope="col">Valor</th>
              <th scope="col">
                <Paginator
                  page={page}
                  perPage={perPage}
                  hasMore={pedidos.length === perPage}
                  next={nextPage}
                  prev={prevPage}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {loadingMore ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  Carregando...
                </td>
              </tr>
            ) : (
              pedidos.map((pedido) => {
                return (
                  <tr
                    key={pedido.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setViewDetails(pedido.id)}
                  >
                    <td style={{ verticalAlign: "middle" }}>#{pedido.id}</td>
                    <td style={{ verticalAlign: "middle" }}>
                      {moment(pedido.created_at).format("DD/MM/YY HH:mm")}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      {pedido.distribuidor}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>{pedido.itens}</td>
                    <td>
                      {pedido.current_status === "cancelado" ? (
                        `Cancelado`
                      ) : (
                        <Stepper step={pedido.current_status} />
                      )}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <FontAwesomeIcon icon={faCoin} /> {pedido.value}
                    </td>
                    <td style={{ verticalAlign: "middle", textAlign: "right" }}>
                      <FontAwesomeIcon icon={faAngleRight} />
                    </td>
                  </tr>
                );
              })
            )}
            {pedidos.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  Nenhum pedido
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </WhiteBox>
      {declineModalOpen && (
        <Modal
          isOpen={declineModalOpen}
          toggle={() => setDeclineModalOpen(false)}
          size="md"
        >
          <ModalHeader>Cancelar Pedido</ModalHeader>
          <ModalBody>
            <Text>Informe o motivo do cancelamento:</Text>
            <Input
              type="text"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Motivo do cancelamento"
            />
            <Button
              onClick={() => {
                _cancelarPedido(toCreate);
                setDeclineModalOpen(false);
              }}
              style={{ marginTop: 10 }}
            >
              Cancelar Pedido
            </Button>
          </ModalBody>
        </Modal>
      )}
      {toCreate && (
        <Modal
          isOpen={true}
          toggle={() => {
            setToCreate(null);
          }}
          size="xl"
        >
          <ModalHeader>Entrada de Pedido</ModalHeader>
          <ModalBody>
            <OrderViewer id_pedido={toCreate} withTimeline={false} />
            <FH>
              <Label>Divisão: </Label>
              <select
                value={centro_de_custo}
                onChange={(e) => setCentroDeCusto(e.target.value)}
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  width: "100%",
                  padding: 10,
                  boder: "1px solid #ccc",
                  marginLeft: 10,
                }}
              >
                {!centro_de_custo && (
                  <option value="">Selecione a divisão:</option>
                )}
                <option value="com-pet">Comercial Pet</option>
                <option value="com-equi">Comercial Equi</option>
                <option value="com-bepet">Comercial Bepet</option>
                <option value="com-aerosol">Comercial Aerosol</option>
                <option value="com-ruminantes">Comercial Ruminantes</option>
                <option value="com-redes">Comercial Redes</option>
                <option value="mkt-pet">Marketing Pet</option>
                <option value="mkt-equi">Marketing Equi</option>
                <option value="mkt-bepet">Marketing Bepet</option>
                <option value="mkt-aerosol">Marketing Aerosol</option>
                <option value="mkt-ruminantes">Marketing Ruminantes</option>
                <option value="mkt-redes">Marketing Redes</option>
                <option value="trd-pet">Trade Pet</option>
                <option value="trd-equi">Trade Equi</option>
                <option value="trd-bepet">Trade Bepet</option>
                <option value="trd-aerosol">Trade Aerosol</option>
                <option value="trd-ruminantes">Trade Ruminantes</option>
                <option value="trd-redes">Trade Redes</option>
                <option value="tec-pet">Técnico Pet</option>
                <option value="tec-equi">Técnico Equi</option>
                <option value="tec-bepet">Técnico Bepet</option>
                <option value="tec-aerosol">Técnico Aerosol</option>
                <option value="tec-ruminantes">Técnico Ruminantes</option>
                <option value="tec-redes">Técnico Redes</option>
              </select>
            </FH>
            <FH>
              <Button
                color="danger"
                onClick={() => {
                  setDeclineReason("");
                  // setToCreate(null);
                  // setIdEverest("");
                  setDeclineModalOpen(true);
                }}
                style={{ marginRight: 10, backgroundColor: "#dc3545" }}
              >
                Cancelar Pedido
              </Button>
              <Button onClick={_registrarEntrada}>
                {registrando ? "Registrando.... " : "Aprovar"}
              </Button>
            </FH>
          </ModalBody>
        </Modal>
      )}
      {viewDetails && (
        <Modal
          isOpen={viewDetails}
          toggle={() => setViewDetails(false)}
          size="xl"
        >
          <ModalHeader>Detalhes do Pedido</ModalHeader>
          <ModalBody>
            <OrderViewer id_pedido={viewDetails} />
          </ModalBody>
        </Modal>
      )}
    </S.Container>
  );
}

export default Pedidos;
